import type { ReactNode } from "react";
import { useViewport } from "../../../hooks/useViewport";
import { KbdChip } from "./modalPrimitives";

export interface SegmentedToggleOption<Value extends string> {
  value: Value;
  icon: ReactNode;
  label: string;
}

interface SegmentedToggleProps<Value extends string> {
  options: SegmentedToggleOption<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  /** Клавишът, който обръща изгледа — chip-ът стои на всеки сегмент
   *  (Плочки | Таблица: F7); без него — без chip и без мястото за него. */
  shortcut?: string;
  /** „bar" — 36px за горната лента (височината ѝ остава 60px); иначе 44px
   *  като бутоните във футъра. */
  size?: "footer" | "bar";
}

/**
 * Сегментираният превключвател на изгледа в „Маркиране": Плочки | Таблица във
 * футъра на продуктите и Списък | Схема в горната лента на масите. Избраният
 * сегмент е маркиран; под 1440px (isWide) е по-стегнат, за да се събере целият
 * футър на 1280.
 */
export const SegmentedToggle = <Value extends string>({
  options,
  value,
  onChange,
  shortcut,
  size = "footer",
}: SegmentedToggleProps<Value>) => {
  const { isWide } = useViewport();
  // Без chip десният отстъп е колкото левия.
  const padding = shortcut
    ? isWide
      ? "pl-3.5 pr-7"
      : "pl-3 pr-6"
    : isWide
      ? "px-3.5"
      : "px-3";

  return (
    <span
      className={`inline-flex items-stretch border border-pos-ctl bg-pos-card overflow-hidden shrink-0 ${
        size === "bar" ? "h-9 rounded-[10px]" : "h-11 rounded-[11px]"
      }`}
    >
      {options.map((option, index) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={`relative inline-flex items-center gap-1.5 font-semibold cursor-pointer transition-colors ${
            index > 0 ? "border-l border-pos-ctl" : ""
          } ${padding} ${isWide ? "text-[14px]" : "text-[13px]"} ${
            value === option.value
              ? "bg-pos-marked text-pos-accent-ink"
              : "text-pos-ink2 hover:bg-pos-panel"
          }`}
        >
          {option.icon}
          {option.label}
          {shortcut && <KbdChip shortcut={shortcut} colorClass="text-pos-faint" />}
        </button>
      ))}
    </span>
  );
};
